import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined';
import TaskAltOutlinedIcon from '@mui/icons-material/TaskAltOutlined';
import { Box, Button, CircularProgress, MenuItem, Select, Stack, TextField, Tooltip, Typography } from '@mui/material';
import dayjs from 'dayjs';
import React, { useState } from 'react';

import { useGetMemberUpdateActivity } from '@/features/engagement/engagement.services';

type ActivityPeriod = '28' | '90' | '365' | 'custom';
export type ResponseFilter = 'all' | 'completed' | 'incomplete';

interface IProps {
  completedResponseCount: number;
  incompleteResponseCount: number;
  responseFilter: ResponseFilter;
  onResponseFilterChange: (filter: ResponseFilter) => void;
}

const changeLabel = (value: number, previous: number) => {
  if (previous === 0) return value === 0 ? 'No change' : 'New';
  const change = Math.round(((value - previous) / previous) * 100);
  if (change === 0) return 'No change';
  return `${change > 0 ? '+' : ''}${change}%`;
};

const MemberUpdateActivityPanel = ({
  completedResponseCount,
  incompleteResponseCount,
  responseFilter,
  onResponseFilterChange
}: IProps) => {
  const [period, setPeriod] = useState<ActivityPeriod>('90');
  const [customFromDate, setCustomFromDate] = useState(dayjs().subtract(89, 'day').format('YYYY-MM-DD'));
  const [customToDate, setCustomToDate] = useState(dayjs().format('YYYY-MM-DD'));
  const [appliedCustomRange, setAppliedCustomRange] = useState<{ fromDate: string; toDate: string } | null>(null);
  const customRangeLength = dayjs(customToDate).diff(dayjs(customFromDate), 'day');
  const customRangeValid = customRangeLength >= 0 && customRangeLength <= 365;
  const selectedDays = (period === 'custom' ? 90 : Number(period)) as 28 | 90 | 365;
  const activity = useGetMemberUpdateActivity(
    selectedDays,
    period === 'custom' ? appliedCustomRange?.fromDate : undefined,
    period === 'custom' ? appliedCustomRange?.toDate : undefined,
    period !== 'custom' || appliedCustomRange !== null
  );
  const data = activity.data;
  const assignedResponseCount = completedResponseCount + incompleteResponseCount;
  const responseRate = assignedResponseCount ? Math.round((completedResponseCount / assignedResponseCount) * 100) : 0;

  const selectResponseFilter = (filter: Exclude<ResponseFilter, 'all'>) => {
    onResponseFilterChange(responseFilter === filter ? 'all' : filter);
  };

  return (
    <Box data-tour="update-activity" sx={{ border: '1px solid #E7E7E5', borderRadius: 3, bgcolor: '#FAF8F5', p: 1.25 }}>
      <Typography variant="body2" sx={{ fontWeight: 700, color: '#1C2024' }}>
        Your update activity
      </Typography>
      <Typography variant="caption" sx={{ color: '#737373' }}>
        See how you are keeping up with assigned Signals.
      </Typography>

      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 1 }}>
        <Box
          component="button"
          type="button"
          aria-label="Filter Focus Areas by incomplete Signal responses"
          aria-pressed={responseFilter === 'incomplete'}
          onClick={() => selectResponseFilter('incomplete')}
          sx={{
            width: 82,
            height: 82,
            p: 0,
            border: 0,
            outline: responseFilter === 'incomplete' ? '2px solid #B95312' : 'none',
            outlineOffset: 2,
            borderRadius: '50%',
            background: '#FFFFFF',
            display: 'grid',
            placeItems: 'center',
            position: 'relative',
            cursor: 'pointer'
          }}
        >
          <CircularProgress
            variant="determinate"
            value={100}
            size={82}
            thickness={4.4}
            sx={{ position: 'absolute', color: '#E4E4E1' }}
          />
          <CircularProgress
            variant="determinate"
            value={responseRate}
            size={82}
            thickness={4.4}
            sx={{ position: 'absolute', color: '#2F7D63' }}
          />
          <Box sx={{ position: 'relative', zIndex: 1, textAlign: 'center' }}>
            <Typography sx={{ fontSize: 17, lineHeight: 1, fontWeight: 700, color: '#2F6B4F' }}>
              {responseRate}%
            </Typography>
            <Typography variant="caption" sx={{ display: 'block', color: '#737373', fontSize: 8.5, lineHeight: 1.1 }}>
              response rate
            </Typography>
          </Box>
        </Box>
        <Stack spacing={0.5} sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {completedResponseCount} of {assignedResponseCount} requested updates completed
          </Typography>
          <Stack direction="row" spacing={0.75}>
            <Button
              size="small"
              variant={responseFilter === 'completed' ? 'contained' : 'outlined'}
              onClick={() => selectResponseFilter('completed')}
              sx={{ minWidth: 0, px: 1, py: 0.25, fontSize: 10, textTransform: 'none' }}
            >
              Completed ({completedResponseCount})
            </Button>
            <Button
              size="small"
              variant={responseFilter === 'incomplete' ? 'contained' : 'outlined'}
              onClick={() => selectResponseFilter('incomplete')}
              sx={{ minWidth: 0, px: 1, py: 0.25, fontSize: 10, textTransform: 'none' }}
            >
              Incomplete ({incompleteResponseCount})
            </Button>
          </Stack>
        </Stack>
      </Stack>

      <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1 }}>
        <Typography variant="caption" sx={{ color: '#737373' }}>
          Update totals for
        </Typography>
        <Select
          size="small"
          value={period}
          onChange={(event) => {
            const nextPeriod = event.target.value as ActivityPeriod;
            setPeriod(nextPeriod);
            if (nextPeriod !== 'custom') setAppliedCustomRange(null);
          }}
          aria-label="Update activity period"
          sx={{ minWidth: 100, height: 30, fontSize: 12, bgcolor: '#FFFFFF' }}
        >
          <MenuItem value="28">28 days</MenuItem>
          <MenuItem value="90">90 days</MenuItem>
          <MenuItem value="365">365 days</MenuItem>
          <MenuItem value="custom">Custom</MenuItem>
        </Select>
      </Stack>

      {period === 'custom' && (
        <Stack spacing={0.75} sx={{ mt: 1 }}>
          <Stack direction="row" spacing={0.75}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="From"
              value={customFromDate}
              onChange={(event) => setCustomFromDate(event.target.value)}
              slotProps={{ inputLabel: { shrink: true } }}
            />
            <TextField
              fullWidth
              size="small"
              type="date"
              label="To"
              value={customToDate}
              onChange={(event) => setCustomToDate(event.target.value)}
              slotProps={{ inputLabel: { shrink: true } }}
            />
          </Stack>
          <Button
            size="small"
            variant="contained"
            disabled={!customRangeValid || activity.isFetching}
            onClick={() => setAppliedCustomRange({ fromDate: customFromDate, toDate: customToDate })}
            sx={{ textTransform: 'none', alignSelf: 'flex-start' }}
          >
            Show results
          </Button>
          {!customRangeValid && (
            <Typography variant="caption" sx={{ color: '#B42318' }}>
              Select a period of no more than 366 days.
            </Typography>
          )}
        </Stack>
      )}

      {activity.isFetching ? (
        <Box sx={{ minHeight: 72, display: 'grid', placeItems: 'center' }}>
          <CircularProgress size={22} />
        </Box>
      ) : data ? (
        <>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 0.75, mt: 0.75 }}>
            {[
              {
                icon: <TaskAltOutlinedIcon sx={{ fontSize: 16, color: '#2F7D63' }} />,
                label: 'Signal updates',
                value: data.requestedUpdatesReceivedCount,
                change: changeLabel(data.requestedUpdatesReceivedCount, data.previousRequestedUpdatesReceivedCount)
              },
              {
                icon: <EditNoteOutlinedIcon sx={{ fontSize: 16, color: '#B95312' }} />,
                label: 'Additional updates',
                value: data.unpromptedUpdateCount,
                change: changeLabel(data.unpromptedUpdateCount, data.previousUnpromptedUpdateCount)
              }
            ].map((item) => (
              <Stack
                key={item.label}
                direction="row"
                alignItems="center"
                spacing={0.75}
                sx={{ bgcolor: '#FFFFFF', borderRadius: 2, px: 1, py: 0.75, minWidth: 0 }}
              >
                {item.icon}
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: 15, lineHeight: 1, fontWeight: 700 }}>{item.value}</Typography>
                  <Typography variant="caption" sx={{ display: 'block', color: '#737373', fontSize: 9.5 }}>
                    {item.label}
                  </Typography>
                  <Tooltip title="Change from the previous equivalent period" enterDelay={500} arrow>
                    <Typography
                      component="span"
                      tabIndex={0}
                      variant="caption"
                      sx={{ display: 'block', width: 'fit-content', color: '#2F6B4F', fontSize: 9, cursor: 'help' }}
                    >
                      {item.change}
                    </Typography>
                  </Tooltip>
                </Box>
              </Stack>
            ))}
          </Box>
        </>
      ) : period === 'custom' ? (
        <Typography variant="caption" sx={{ display: 'block', mt: 1, color: '#737373' }}>
          Choose dates, then select Show results.
        </Typography>
      ) : null}
    </Box>
  );
};

export default MemberUpdateActivityPanel;
